import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createResetSessionPrivateKey } from '@/features/auth/api/reset-session.ts';
import { getInviteUser, getInviteDetails, getInviteScope, updateInviteStatus, createInviteUser } from '@/features/auth/api/user-invite.ts';
import { createPhone, listPhones, submitPhoneToken, resendPhoneToken } from '@/features/auth/api/phone.ts';
import { derivePrivateKey, getPublicKey } from '@/shared/lib/crypto.ts';
import { PBKDF2_SALT, PBKDF2_ITERATIONS } from '@/shared/lib/auth-config.ts';
import { parseBackendErrors } from '@/shared/lib/backend-errors';
import { exitToLogin } from '@/shared/lib/session-exit';
import { isUnreadableResponseError } from '@/shared/lib/response-shape';
import type { InviteUser, InviteDetails, InviteScope } from '@/features/auth/api/user-invite.ts';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { FormField } from '@/shared/ui/form-field';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { LanguageToggle } from '@/shared/ui/language-toggle';
import { Logo } from '@/shared/ui/logo';
import { PhoneInput } from '@/shared/ui/phone-input';
import { useToast } from '@/shared/hooks/use-toast';
import { useLanguage } from '@/shared/hooks/use-language';
import { DebugLogger } from '@/shared/lib/debug';

const USER_INVITE_FIELD_MAP: Record<string, string> = {
  tax_id: 'taxId',
  name: 'name',
  public_key: 'password',
};
import {
  DEFAULT_PHONE_COUNTRY_ISO,
  findPhoneCountryByDialCode,
  findPhoneCountryByIso,
  formatNationalNumber,
  PHONE_COUNTRIES,
} from '@/shared/lib/phone-countries';

function UserInvite() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { inviteId } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [existingUser, setExistingUser] = useState<InviteUser | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [inviteScope, setInviteScope] = useState<InviteScope | null>(null);
  
  // Form state for new user creation
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Phone-verification state (verify-then-form)
  const [phonePhase, setPhonePhase] = useState<'enter' | 'verify' | 'done'>('enter');
  const [phoneCountryIso, setPhoneCountryIso] = useState<string>(DEFAULT_PHONE_COUNTRY_ISO);
  const [phoneNationalDigits, setPhoneNationalDigits] = useState('');
  const [phoneId, setPhoneId] = useState<string | null>(null);
  const [phoneToken, setPhoneToken] = useState('');
  const [phoneIsLoading, setPhoneIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [resendCooldownUntil, setResendCooldownUntil] = useState<number>(0);

  const phoneCountry =
    findPhoneCountryByIso(phoneCountryIso) ?? PHONE_COUNTRIES[0];
  const phoneDisplay = `+${phoneCountry.dialCode} ${formatNationalNumber(
    phoneNationalDigits,
    phoneCountry.mask,
  )}`.trim();

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteId) return;
    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');
    if (!sessionId || !sessionSecret) {
      setPhoneError(t('userInvite.missingParams') || 'Missing session parameters');
      return;
    }

    const cleanCode = phoneCountry.dialCode;
    const cleanNumber = phoneNationalDigits;
    if (!cleanCode || !cleanNumber) {
      setPhoneError(t('userInvite.phoneRequired') || 'Phone number is required');
      return;
    }

    setPhoneIsLoading(true);
    setPhoneError(null);
    try {
      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      const result = await createPhone(sessionId, sessionPrivateKey, cleanCode, cleanNumber);
      const created = result.phones?.[0];
      if (!created) {
        throw new Error('Backend did not return a phone resource');
      }
      setPhoneId(created.id);
      setResendCooldownUntil(Date.now() + 60_000);
      setPhonePhase('verify');
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to start phone verification');
    } finally {
      setPhoneIsLoading(false);
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteId || !phoneId) return;
    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');
    if (!sessionId || !sessionSecret) {
      setPhoneError(t('userInvite.missingParams') || 'Missing session parameters');
      return;
    }
    if (phoneToken.trim().length !== 6) {
      setPhoneError(t('userInvite.tokenLength') || 'Enter the 6-digit code');
      return;
    }

    setPhoneIsLoading(true);
    setPhoneError(null);
    try {
      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      await submitPhoneToken(phoneId, sessionId, sessionPrivateKey, phoneToken.trim());
      setPhonePhase('done');
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Invalid token');
    } finally {
      setPhoneIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!phoneId) return;
    if (Date.now() < resendCooldownUntil) return;
    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');
    if (!sessionId || !sessionSecret) {
      setPhoneError(t('userInvite.missingParams') || 'Missing session parameters');
      return;
    }
    setPhoneIsLoading(true);
    setPhoneError(null);
    try {
      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      await resendPhoneToken(phoneId, sessionId, sessionPrivateKey);
      setResendCooldownUntil(Date.now() + 60_000);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setPhoneIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeInvite = async () => {
      try {
        if (!inviteId) {
          setError(t("userInvite.invalidLink") || "Invalid invitation link");
          setIsInitializing(false);
          return;
        }

        // Get session_id and session_secret from query params
        const sessionId = searchParams.get('session_id');
        const sessionSecret = searchParams.get('session_secret');

        if (!sessionId || !sessionSecret) {
          setError(t("userInvite.missingParams") || "Missing session parameters");
          setIsInitializing(false);
          return;
        }

        // Create reset session private key from session_secret
        const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);

        // Get invite details to get the email and scope information
        // If this fails (e.g., expired/unavailable), we should show the error and NOT show the form
        const inviteDetailsResponse = await getInviteDetails(inviteId, sessionId, sessionPrivateKey);
        const invite = inviteDetailsResponse.invite;
        const email = invite?.email;
        
        if (!email) {
          setError(t("userInvite.emailNotFound") || "Email not found in invitation");
          setIsInitializing(false);
          return;
        }

        if (!invite) {
          setError(t("userInvite.invalidInvite") || "Invalid invitation");
          setIsInitializing(false);
          return;
        }

        // Only proceed if invite details were successfully fetched
        setInviteEmail(email);
        setInviteDetails(invite);

        try {
          const phoneResponse = await listPhones(sessionId, sessionPrivateKey, 'active');
          const activePhone = phoneResponse.phones?.[0];
          if (activePhone) {
            const matchedCountry = findPhoneCountryByDialCode(activePhone.code);
            if (matchedCountry) setPhoneCountryIso(matchedCountry.iso);
            setPhoneNationalDigits(activePhone.number);
            setPhoneId(activePhone.id);
            setPhonePhase('done');
          }
        } catch (phoneListError) {
          DebugLogger.warn('Failed to fetch existing phones; will require fresh verification', { phoneListError });
        }

        try {
          const scopeResponse = await getInviteScope(inviteId, sessionId, sessionPrivateKey);
          setInviteScope(scopeResponse.scopes[0] ?? null);
        } catch (scopeError) {
          DebugLogger.warn('Failed to fetch invite scope; falling back to raw scope_id', { scopeError });
        }

        // Check if user already exists
        const userResponse = await getInviteUser(inviteId, sessionId, sessionPrivateKey);

        DebugLogger.debug('Invite user check completed', {
          inviteId,
          email,
          scopeType: invite.scope_type,
          scopeId: invite.scope_id,
          usersCount: userResponse.users?.length || 0
        });

        if (userResponse.users && userResponse.users.length > 0) {
          // User already exists - show confirmation page
          setExistingUser(userResponse.users[0]);
        } else {
          // User doesn't exist - will show creation form
          // Only show form if invite details were successfully fetched
          setExistingUser(null);
        }
      } catch (error) {
        DebugLogger.error('Failed to initialize invite', { error });
        if (isUnreadableResponseError(error)) {
          setError(t("errors.unreadableResponse") || "Could not read the server's response. Please try again.");
        } else {
          setError(error instanceof Error ? error.message : 'Failed to initialize invitation');
        }
      } finally {
        setIsInitializing(false);
      }
    };

    initializeInvite();
  }, [inviteId, searchParams]);

  const handleAcceptInvite = async () => {
    if (!inviteId) return;

    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');

    if (!sessionId || !sessionSecret) {
      setError(t("userInvite.missingParams") || "Missing session parameters");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      const result = await updateInviteStatus(inviteId, sessionId, sessionPrivateKey, 'processing');

      setSuccess(true);
      toast({
        title: t("userInvite.inviteAccepted") || "Invitation Accepted",
        description: result.message || t("userInvite.inviteAcceptedDesc") || "You have successfully accepted the invitation.",
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        exitToLogin();
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation';
      setError(errorMessage);
      toast({
        title: t("userInvite.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDenyInvite = async () => {
    if (!inviteId) return;

    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');

    if (!sessionId || !sessionSecret) {
      setError(t("userInvite.missingParams") || "Missing session parameters");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      await updateInviteStatus(inviteId, sessionId, sessionPrivateKey, 'canceled');

      setSuccess(true);
      toast({
        title: t("userInvite.inviteDenied") || "Invitation Denied",
        description: t("userInvite.inviteDeniedDesc") || "You have declined the invitation.",
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        exitToLogin();
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deny invitation';
      setError(errorMessage);
      toast({
        title: t("userInvite.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteId || !inviteEmail) return;

    // Validate form
    if (!name.trim()) {
      setError(t("userInvite.nameRequired") || "Name is required");
      return;
    }

    if (!taxId.trim()) {
      setError(t("userInvite.taxIdRequired") || "Tax ID (CPF) is required");
      return;
    }

    // Basic CPF validation (11 digits)
    const cleanTaxId = taxId.replace(/\D/g, '');
    if (cleanTaxId.length !== 11) {
      setError(t("userInvite.invalidTaxId") || "Invalid CPF. Please enter 11 digits");
      return;
    }

    // Validate password
    if (!password.trim()) {
      setError(t("userInvite.passwordRequired") || "Password is required");
      return;
    }

    if (password !== confirmPassword) {
      setError(t("userInvite.passwordMismatch") || "Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError(t("userInvite.passwordMinLength") || "Password must be at least 8 characters");
      return;
    }

    const sessionId = searchParams.get('session_id');
    const sessionSecret = searchParams.get('session_secret');

    if (!sessionId || !sessionSecret) {
      setError(t("userInvite.missingParams") || "Missing session parameters");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Derive private key from email, password, and salt (same as login/reset password)
      const privateKey = await derivePrivateKey(inviteEmail, password, PBKDF2_SALT, PBKDF2_ITERATIONS);
      
      // Get public key from private key
      const publicKey = getPublicKey(privateKey);
      
      // Convert public key to PEM format
      const publicKeyPem = publicKey.toPem();

      const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);
      
      const result = await createInviteUser(
        inviteId,
        sessionId,
        sessionPrivateKey,
        {
          name: name.trim(),
          tax_id: cleanTaxId,
          public_key: publicKeyPem
        }
      );

      setSuccess(true);
      toast({
        title: t("userInvite.userCreated") || "User Created",
        description: result.message || t("userInvite.userCreatedDesc") || "Your user has been created successfully.",
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        exitToLogin();
      }, 3000);
    } catch (err) {
      setFieldErrors({});

      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        USER_INVITE_FIELD_MAP,
      );

      if (Object.keys(backendFieldErrors).length > 0 || generalError) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
        setFieldErrors(backendFieldErrors);
        setError(generalError ?? errorMessage);
        toast({
          title: t("userInvite.error") || "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
        setError(errorMessage);
        toast({
          title: t("userInvite.error") || "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("userInvite.title") || "Invitation"}
            </h1>
            <p className="text-muted-foreground">
              {t("userInvite.initializing") || "Loading invitation..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("userInvite.successTitle") || "Success"}
            </h1>
          </div>

          <div className="bg-card p-8 rounded-2xl border border-border card-hover">
            <div data-testid="invite-success" className="bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded-md text-sm mb-6">
              <p className="font-semibold mb-2">{t("userInvite.successMessage") || "Operation completed successfully"}</p>
              <p className="text-sm">
                {t("userInvite.redirecting") || "Redirecting to login page..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User exists - show confirmation page
  if (existingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("userInvite.title") || "Invitation"}
            </h1>
            <p className="text-muted-foreground">
              {t("userInvite.subtitle") || "You have been invited to join"}
            </p>
          </div>

          <div className="bg-card p-8 rounded-2xl border border-border card-hover">
            {error && (
              <div data-testid="invite-error" className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm mb-6">
                {error}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {t("userInvite.userInfo") || "User Information"}
                </p>
                <p className="text-lg font-semibold text-foreground">
                  {existingUser.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {existingUser.reference_id || existingUser.email || existingUser.id}
                </p>
              </div>
              
              {inviteDetails && (inviteDetails.scope_type || inviteDetails.scope_id) && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t("userInvite.invitedTo") || "You are being invited to:"}
                  </p>
                  <div className="space-y-1">
                    {inviteDetails.scope_type && (
                      <p className="text-sm text-foreground">
                        <span className="font-semibold">
                          {inviteDetails.scope_type === 'account'
                            ? (t("userInvite.account") || "Account")
                            : (t("userInvite.accountHolder") || "Organization")
                          }:
                        </span>{' '}
                        <span className="text-muted-foreground">
                          {inviteScope?.name || inviteDetails.scope_id}
                        </span>
                      </p>
                    )}
                    {!inviteDetails.scope_type && inviteDetails.scope_id && (
                      <p className="text-sm text-foreground">
                        <span className="font-semibold">
                          {t("userInvite.scopeId") || "Scope ID"}:
                        </span>{' '}
                        <span className="text-muted-foreground">
                          {inviteScope?.name || inviteDetails.scope_id}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Button
                data-testid="invite-accept"
                onClick={handleAcceptInvite}
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isLoading ? (t("userInvite.processing") || "Processing...") : (t("userInvite.accept") || "Accept Invitation")}
              </Button>
              <Button
                data-testid="invite-deny"
                onClick={handleDenyInvite}
                disabled={isLoading}
                variant="outline"
                className="w-full"
              >
                {t("userInvite.deny") || "Deny Invitation"}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => exitToLogin()}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t("userInvite.backToLogin") || "Back to Login"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User doesn't exist - show creation form only if invite details were successfully fetched.
  // The dedicated error-only screen is reserved for init-time failures (no inviteEmail yet);
  // post-init errors (validation, backend rejects on submit) render inline on the form so the
  // user can correct and retry.
  if (error && !inviteEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("userInvite.title") || "Invitation"}
            </h1>
          </div>

          <div className="bg-card p-8 rounded-2xl border border-border card-hover">
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm mb-6">
              {error}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => exitToLogin()}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t("userInvite.backToLogin") || "Back to Login"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Phone verification must complete before the creation form is shown.
  if (phonePhase !== 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {phonePhase === 'enter'
                ? (t('userInvite.verifyPhoneTitle') || 'Verify your phone')
                : (t('userInvite.enterTokenTitle') || 'Enter the code we sent you')}
            </h1>
            <p className="text-muted-foreground">
              {phonePhase === 'enter'
                ? (t('userInvite.verifyPhoneDesc') || "We'll send a 6-digit code by SMS to confirm your number.")
                : (t('userInvite.enterTokenDesc') || 'We sent a 6-digit code to {phone}. Enter it below.').replace('{phone}', phoneDisplay)}
            </p>
          </div>

          <div className="bg-card p-8 rounded-2xl border border-border card-hover">
            {phoneError && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm mb-6">
                {phoneError}
              </div>
            )}

            {phonePhase === 'enter' ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">{t('userInvite.phoneNumber') || 'Phone number'}</Label>
                  <PhoneInput
                    id="phoneNumber"
                    countryIso={phoneCountryIso}
                    nationalDigits={phoneNationalDigits}
                    onCountryChange={(c) => setPhoneCountryIso(c.iso)}
                    onNationalDigitsChange={setPhoneNationalDigits}
                    disabled={phoneIsLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
                  disabled={phoneIsLoading || !phoneNationalDigits}
                >
                  {phoneIsLoading ? (t('userInvite.sending') || 'Sending...') : (t('userInvite.sendCode') || 'Send code')}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleTokenSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="phoneToken">{t('userInvite.token') || '6-digit code'}</Label>
                  <Input
                    id="phoneToken"
                    type="tel"
                    inputMode="numeric"
                    placeholder="000000"
                    value={phoneToken}
                    onChange={(e) => setPhoneToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-background border-border tracking-widest text-center"
                    disabled={phoneIsLoading}
                    maxLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
                  disabled={phoneIsLoading || phoneToken.length !== 6}
                >
                  {phoneIsLoading ? (t('userInvite.verifying') || 'Verifying...') : (t('userInvite.verify') || 'Verify')}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={phoneIsLoading || Date.now() < resendCooldownUntil}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                  >
                    {Date.now() < resendCooldownUntil
                      ? (t('userInvite.resendCooldown') || 'Resend available in {seconds}s').replace('{seconds}', String(Math.ceil((resendCooldownUntil - Date.now()) / 1000)))
                      : (t('userInvite.resend') || 'Resend code')}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => exitToLogin()}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('userInvite.backToLogin') || 'Back to Login'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show creation form only if no errors and user doesn't exist
  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("userInvite.createUser") || "Create User"}
          </h1>
          <p className="text-muted-foreground">
            {t("userInvite.createUserDesc") || "Complete your registration to accept the invitation"}
          </p>
        </div>

        <div className="bg-card p-8 rounded-2xl border border-border card-hover">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleCreateUser} className="space-y-6">
            <FormField
              id="name"
              type="text"
              label={t("userInvite.name") || "Full Name"}
              placeholder={t("userInvite.namePlaceholder") || "Enter your full name"}
              value={name}
              onChange={(value) => {
                setName(value);
                if (fieldErrors.name) {
                  const next = { ...fieldErrors };
                  delete next.name;
                  setFieldErrors(next);
                  if (Object.keys(next).length === 0) setError(null);
                }
              }}
              error={fieldErrors.name}
              disabled={isLoading}
            />

            <FormField
              id="taxId"
              type="text"
              label={t("userInvite.taxId") || "CPF (Tax ID)"}
              placeholder={t("userInvite.taxIdPlaceholder") || "000.000.000-00"}
              value={taxId}
              onChange={(value) => {
                setTaxId(value);
                if (fieldErrors.taxId) {
                  const next = { ...fieldErrors };
                  delete next.taxId;
                  setFieldErrors(next);
                  if (Object.keys(next).length === 0) setError(null);
                }
              }}
              error={fieldErrors.taxId}
              hint={t("userInvite.taxIdHint") || "Enter your Brazilian CPF (11 digits)"}
              disabled={isLoading}
              maxLength={14}
            />

            <div className="bg-muted/50 border border-border rounded-md px-4 py-3 text-sm flex items-center gap-2">
              <span className="text-muted-foreground">{t("userInvite.phoneVerified") || "Verified phone:"}</span>
              <span className="text-base leading-none">{phoneCountry.flag}</span>
              <span className="font-medium">{phoneDisplay}</span>
            </div>

            <FormField
              id="password"
              type={showPassword ? "text" : "password"}
              label={t("userInvite.password") || "Password"}
              placeholder="••••••••"
              value={password}
              onChange={(value) => {
                setPassword(value);
                if (fieldErrors.password) {
                  const next = { ...fieldErrors };
                  delete next.password;
                  setFieldErrors(next);
                  if (Object.keys(next).length === 0) setError(null);
                }
              }}
              error={fieldErrors.password}
              disabled={isLoading}
              autoComplete="new-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <FormField
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              label={t("userInvite.confirmPassword") || "Confirm Password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                if (fieldErrors.confirmPassword) {
                  const next = { ...fieldErrors };
                  delete next.confirmPassword;
                  setFieldErrors(next);
                  if (Object.keys(next).length === 0) setError(null);
                }
              }}
              error={fieldErrors.confirmPassword}
              disabled={isLoading}
              autoComplete="new-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
              disabled={isLoading}
            >
              {isLoading ? (t("userInvite.creating") || "Creating...") : (t("userInvite.createUser") || "Create User")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => exitToLogin()}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t("userInvite.backToLogin") || "Back to Login"}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <LanguageToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserInvite;

